import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { CompletedTransactions } from '@sorare/marketplace/src/components/user/CompletedTransactions';

import type {
  UserTransactionsQuery,
  UserTransactionsQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

export const USER_TRANSACTIONS_QUERY = gql`
  query UserTransactionsQuery($slug: String!, $sport: Sport!, $after: String) {
    user(slug: $slug) {
      slug
      trades(sport: [$sport], after: $after) {
        pageInfo {
          endCursor
          hasNextPage
        }
        nodes {
          ...CompletedTransactions_deal
        }
      }
    }
  }
  ${CompletedTransactions.fragments.deal}
` as TypedDocumentNode<UserTransactionsQuery, UserTransactionsQueryVariables>;

export const loader = queryLoader<PageParams>(
  USER_TRANSACTIONS_QUERY,
  ({ slug, sport }) => ({ slug, sport })
);

const StyledFullWidthContainer = styled(FullWidthContainer)`
  padding-top: var(--double-unit);
`;

const UserTransactionsPage = () => {
  const { data, loading, loadMore } = useQueryLoader<UserTransactionsQuery>();

  if (!data?.user) return null;

  const { user } = data;
  const { trades } = user;

  return (
    <StyledFullWidthContainer>
      <CompletedTransactions
        transactions={trades.nodes?.filter(Boolean) || []}
        user={user}
        loading={loading}
        hasMore={!!trades?.pageInfo?.hasNextPage}
        fetch={() =>
          loadMore(
            false,
            { after: trades?.pageInfo?.endCursor },
            'TokenDealConnection'
          )
        }
      />
    </StyledFullWidthContainer>
  );
};

export default UserTransactionsPage;
